import { Backdrop, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOrgOptions } from '../../../provider/OrganizationOptionsProvider';
import {
    useGetNestedLinkedRentalSessions,
    useRentalCategories,
    useRentals,
} from '../../../provider/RentalSessionsWrapperProvider';
import PaymentMethodInputs from '../../../shared/PaymentMethodInputs/PaymentMethodInputs';
import { sendClearAllNotifications } from '../../../shared/utils/tvOverlayUtils';
import { calculateMenuPrice, calculatePrice, roundValue } from '../../rentalSessionUtils';
import '../RentalSessionPopUps.css';
import './RentalSessionEndPopUp.css';
import { calculateFitpassValue } from '../../../../utils/calculateFitpassValue';

const emptyResult = [];

// TODO Too many re-renders on Mimdinare case
function RentalSessionEndPopUp({
    setEndPopUp,
    session,
    totalPrice,
    endSession,
    prepaidBalance,
    updateExpiredSessions,
    timestamp,
    onError,
}) {
    const { categoriesMap } = useRentalCategories();
    const orgOptions = useOrgOptions();
    const [payments, setPayments] = useState({
        cash: 0,
        credit: 0,
        fitpass: 0,
    });

    const [isLoading, setIsLoading] = useState(false);
    const { rentalsById } = useRentals();
    const [selectedDiscountIndex, setSelectedDiscountIndex] = useState('0');

    const { data: linkedRentalSessions = emptyResult } = useGetNestedLinkedRentalSessions(
        session.linkedRentalSessions,
        onError
    );

    const discounts = useMemo(() => {
        return categoriesMap[session.rentalCategoryId]?.discounts || [];
    }, [categoriesMap, session.rentalCategoryId]);

    useEffect(() => {
        if (!session.selectedDiscount) {
            setSelectedDiscountIndex('0');
            return;
        }

        const currentSelectedDiscountIndex = discounts.findIndex(
            (discount) =>
                discount?.percent?.$numberDecimal === session.selectedDiscount?.$numberDecimal
        );

        setSelectedDiscountIndex(currentSelectedDiscountIndex + 1);
    }, [session, discounts]);

    const discountedTotalPrice = useMemo(() => {
        return roundValue(
            parseFloat(totalPrice) *
                (1 -
                    (selectedDiscountIndex !== '0'
                        ? discounts[selectedDiscountIndex - 1].percent.$numberDecimal
                        : 0))
        );
    }, [totalPrice, selectedDiscountIndex, discounts]);

    const linkedSessionsPrices = useMemo(() => {
        const prices = {};
        prices.totalPrice = linkedRentalSessions.length
            ? linkedRentalSessions.reduce((previousSum, linkedSession) => {
                  const sessionPrice =
                      parseFloat(
                          calculatePrice(
                              (linkedSession.endTimestamp - linkedSession.startTimestamp) /
                                  1000 /
                                  60,
                              linkedSession.rentalCategoryId,
                              linkedSession.rentalTypes,
                              linkedSession.transferHistory,
                              categoriesMap
                          )
                      ) +
                      parseFloat(calculateMenuPrice(linkedSession.usedProducts)) -
                      linkedSession.paymentCash.$numberDecimal -
                      linkedSession.paymentCredit.$numberDecimal -
                      linkedSession.paymentFitpass.$numberDecimal;

                  prices[linkedSession._id] = sessionPrice;

                  return previousSum + sessionPrice;
              }, 0)
            : 0;

        return prices;
    }, [categoriesMap, linkedRentalSessions]);

    const curTotalPrice = useMemo(() => {
        return parseFloat(
            discountedTotalPrice -
                parseFloat(prepaidBalance.totalPrepaid) +
                roundValue(linkedSessionsPrices.totalPrice, orgOptions)
        );
    }, [
        linkedSessionsPrices.totalPrice,
        orgOptions,
        prepaidBalance.totalPrepaid,
        discountedTotalPrice,
    ]);

    const isPaymentEnough = useMemo(() => {
        return (
            parseFloat(payments.cash) +
                parseFloat(payments.credit) +
                parseFloat(payments.fitpass) >=
                curTotalPrice ||
            (curTotalPrice < 0 &&
                parseFloat(payments.cash) +
                    parseFloat(payments.credit) +
                    parseFloat(payments.fitpass) >
                    curTotalPrice)
        );
    }, [curTotalPrice, payments.cash, payments.credit, payments.fitpass]);

    const handlePaymentsInput = useCallback(
        (paymentInputs) => {
            setPayments({
                ...paymentInputs,
                fitpass: calculateFitpassValue(
                    orgOptions.fitpass?.$numberDecimal,
                    paymentInputs.fitpass
                ),
            });
        },
        [setPayments, orgOptions.fitpass]
    );

    const handleEnd = useCallback(async () => {
        setIsLoading(true);

        let paymentCash = payments.cash;
        let paymentCredit = payments.credit;
        let paymentFitpass = payments.fitpass;

        const linkedRentalSessionsPayments = Object.keys(linkedSessionsPrices)
            .filter((key) => key !== 'totalPrice')
            .map((sessionId) => {
                const curLinkedPrice = linkedSessionsPrices[sessionId];
                const linkedSessionPayments = { sessionId };
                let remainingPrice = curLinkedPrice;

                // Step 1: Pay with cash first
                if (paymentCash > 0 && remainingPrice > 0) {
                    const cashUsed = Math.min(paymentCash, remainingPrice);
                    linkedSessionPayments.paymentCash = cashUsed;
                    paymentCash -= cashUsed;
                    remainingPrice -= cashUsed;
                } else {
                    linkedSessionPayments.paymentCash = 0;
                }

                // Step 2: Pay remaining with credit
                if (paymentCredit > 0 && remainingPrice > 0) {
                    const creditUsed = Math.min(paymentCredit, remainingPrice);
                    linkedSessionPayments.paymentCredit = creditUsed;
                    paymentCredit -= creditUsed;
                    remainingPrice -= creditUsed;
                } else {
                    linkedSessionPayments.paymentCredit = 0;
                }

                // Step 3: Pay remaining with fitpass
                if (paymentFitpass > 0 && remainingPrice > 0) {
                    const fitpassUsed = Math.min(paymentFitpass, remainingPrice);
                    linkedSessionPayments.paymentFitpass = fitpassUsed;
                    paymentFitpass -= fitpassUsed;
                    remainingPrice -= fitpassUsed;
                } else {
                    linkedSessionPayments.paymentFitpass = 0;
                }

                return linkedSessionPayments;
            });

        const selectedDiscount =
            selectedDiscountIndex !== '0' ? discounts[selectedDiscountIndex - 1] : undefined;

        if (
            await endSession({
                sessionId: session._id,
                endTimestamp: timestamp,
                paymentCash,
                paymentCredit,
                paymentFitpass,
                linkedRentalSessionsPayments,
                selectedDiscount: selectedDiscount?.percent?.$numberDecimal ?? undefined,
                ...(selectedDiscount
                    ? {
                          message: `Used discount - ${selectedDiscount.label} : ${selectedDiscount.percent.$numberDecimal}`,
                      }
                    : {}),
            })
        ) {
            sendClearAllNotifications(rentalsById[session.rentalId]?.address);
            setEndPopUp(false);
            updateExpiredSessions(session, 'REMOVE');
        }
        setIsLoading(false);
    }, [
        payments.cash,
        payments.credit,
        payments.fitpass,
        linkedSessionsPrices,
        endSession,
        session,
        timestamp,
        rentalsById,
        setEndPopUp,
        updateExpiredSessions,
        selectedDiscountIndex,
        discounts,
    ]);

    const handleCancel = useCallback(() => {
        if (!isLoading) {
            setEndPopUp(false);
        }
    }, [isLoading, setEndPopUp]);

    return (
        <>
            <div className="popUp-wrapper rentalCard-sessionEnd">
                <div className="totalPriceSection">
                    <span>Total Price : </span>
                    <span className="endPopUp-priceIndicators">
                        {discountedTotalPrice.toFixed(2)}
                        {orgOptions.currency.sign}
                    </span>
                </div>

                {session.linkedRentalSessions.length ? (
                    <div className="totalPriceSection">
                        <span>Linked Sessions price : </span>
                        <span className="endPopUp-priceIndicators">
                            {parseFloat(linkedSessionsPrices.totalPrice).toFixed(2)}
                            {orgOptions.currency.sign}
                        </span>
                    </div>
                ) : null}

                <div className="totalPriceSection">
                    <span>Price to Pay : </span>
                    <span className="endPopUp-priceIndicators">
                        {curTotalPrice.toFixed(2)}
                        {orgOptions.currency.sign}
                    </span>
                </div>

                {categoriesMap[session.rentalCategoryId].discounts.length > 0 && (
                    <div>
                        <p style={{ textAlign: 'center' }}>Select discount</p>
                        <div className="sessionSetup-price sessionSetup-item promo-code">
                            <select
                                value={selectedDiscountIndex}
                                onChange={(e) => {
                                    setSelectedDiscountIndex(e.target.value);
                                }}>
                                <option value={'0'}>No Discount</option>
                                {categoriesMap[session.rentalCategoryId].discounts.map(
                                    (discount, index) => (
                                        <option
                                            selected={
                                                selectedDiscountIndex === (index + 1).toString()
                                            }
                                            key={discount.label}
                                            value={(index + 1).toString()}>
                                            {discount.label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>
                )}

                <PaymentMethodInputs
                    maxTotalValue={curTotalPrice}
                    onInput={handlePaymentsInput}
                    strictMode={true}
                />
                <div className="sessionPopUp-actions">
                    <button
                        className="popUp-button cancel"
                        disabled={isLoading}
                        onClick={handleCancel}>
                        Cancel
                    </button>
                    <button
                        className="popUp-button start"
                        onClick={handleEnd}
                        disabled={isLoading || !isPaymentEnough}>
                        Done {isLoading ? <CircularProgress className="circularProgress" /> : null}
                    </button>
                </div>
            </div>

            <Backdrop className="alert-backdrop" open={true} onClick={handleCancel} />
        </>
    );
}

export default RentalSessionEndPopUp;

